/**
 * Spec 087 (gap 087-1) — Start-Analysis dialog.
 *
 * Single-submit flow (there is no separate dry-run endpoint — POST both
 * triages the sitemap and persists the analysis row on success). The
 * dialog surfaces a 402 `budget_exceeded` response inline so the user
 * can adjust tier budgets / tick the override checkbox (if admin) and
 * resubmit without losing form state.
 *
 * Keyed state:
 *   - sitemap_url: optional — blank falls back to `${brand.domain}/sitemap.xml`
 *   - tier_3_budget: 0..500 slider
 *   - include_tier_4: checkbox
 *   - allow_budget_override: checkbox, admin-only
 */
import { useState } from 'react';
import type { FormEvent, RefObject } from 'react';
import { Spinner } from '../ui/Icon.js';
import { Button } from '../ui/Button.js';
import type { Brand } from '../../types/index.js';
import { useAuth } from '../../lib/auth-context.js';
import { useDialogA11y } from '../../hooks/useDialogA11y.js';
import {
  startSitemapAnalysis,
  type BudgetExceededResponse,
} from '../../api/sitemapAnalyses.js';

interface StartAnalysisDialogProps {
  brand: Brand;
  onCancel: () => void;
  onCreated: (analysisId: string) => void;
  onFlagDisabled: (flag: string) => void;
}

/**
 * Phase 9 Session E.1 — translate raw API error envelopes into user-facing
 * copy. Returns a `{ message, detail? }` pair so the banner can render a
 * bold lead line plus a softer follow-up. Falls back to the raw `error`
 * string for shapes we don't recognise.
 */
function friendlyError(
  body: { error?: string; reason?: string; details?: string[] | string } | null,
): {
  message: string;
  detail?: string;
} {
  if (!body) return { message: 'Unknown error' };
  switch (body.error) {
    case 'sitemap_fetch_failed':
      return {
        message: 'Could not fetch the sitemap.',
        detail: body.reason
          ? `Reason: ${body.reason}. Check the URL is reachable and try again.`
          : 'Check the URL is reachable and try again.',
      };
    case 'budget_exceeded':
      return {
        message: 'This run would exceed your monthly cost cap.',
        detail: 'Adjust the Tier-3 budget or enable the admin bypass below.',
      };
    case 'admin_role_required_for_budget_override':
      return { message: 'Only admins can bypass the monthly budget cap.' };
    case 'sitemap_analysis_disabled':
      return {
        message:
          'Sitemap analysis is currently disabled. Ask an admin to enable `flag.worker.sitemap_analysis`.',
      };
    case 'Validation failed':
      return {
        message: 'Validation failed.',
        detail: Array.isArray(body.details)
          ? body.details.join('; ')
          : body.details ?? undefined,
      };
    default:
      return {
        message: body.error ?? 'Unknown error',
        detail: typeof body.reason === 'string' ? body.reason : undefined,
      };
  }
}

export function StartAnalysisDialog({
  brand,
  onCancel,
  onCreated,
  onFlagDisabled,
}: StartAnalysisDialogProps) {
  const { hasRole } = useAuth();
  const canOverride = hasRole('admin') || hasRole('orbit:admin');

  // Phase 9 Session E.2 (gap 087-R2-1) — Esc-to-close, initial focus on the
  // sitemap URL input, and Tab/Shift+Tab focus trap. Centralised in
  // `useDialogA11y` so the next dialog (DeprecateModal etc.) gets the same
  // behaviour for free.
  const { dialogRef, firstFocusableRef } = useDialogA11y({
    onClose: onCancel,
    isOpen: true,
  });

  const [sitemapUrl, setSitemapUrl] = useState('');
  const [tier3Budget, setTier3Budget] = useState<number>(50);
  const [includeTier4, setIncludeTier4] = useState(false);
  const [allowOverride, setAllowOverride] = useState(false);
  const [pending, setPending] = useState(false);
  const [inlineError, setInlineError] = useState<{
    message: string;
    detail?: string;
  } | null>(null);
  const [budgetExceeded, setBudgetExceeded] = useState<BudgetExceededResponse | null>(
    null,
  );

  // Phase 9 Session E.1 — placeholder/helper-text priority:
  //   1. brand.sitemap_url (explicit override on the Brand record)
  //   2. https://{brand.domain}/sitemap.xml (synthesised default)
  //   3. example.com fallback when the brand has no domain
  const synthesizedDefault = brand.domain
    ? `https://${brand.domain}/sitemap.xml`
    : '';
  const placeholderUrl =
    brand.sitemap_url ?? (synthesizedDefault || 'https://example.com/sitemap.xml');
  const placeholderForInput = placeholderUrl;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setInlineError(null);
    setBudgetExceeded(null);
    try {
      const result = await startSitemapAnalysis(brand.id, {
        sitemap_url: sitemapUrl.trim() === '' ? undefined : sitemapUrl.trim(),
        tier_budgets: { tier_3: tier3Budget },
        include_tier_4: includeTier4,
        allow_budget_override: allowOverride,
      });

      switch (result.kind) {
        case 'created':
          onCreated(result.data.analysis_id);
          return;
        case 'budget_exceeded':
          setBudgetExceeded(result.data);
          return;
        case 'admin_required':
          setInlineError(
            friendlyError({ error: 'admin_role_required_for_budget_override' }),
          );
          return;
        case 'flag_disabled':
          onFlagDisabled(result.flag);
          return;
        case 'validation_error': {
          // The api client collapsed `error` and `details` into a single
          // string of the form `"Validation failed: <details>"`. Split it
          // back so `friendlyError` can produce the two-line banner.
          const colonIdx = result.message.indexOf(':');
          if (colonIdx > -1) {
            setInlineError(
              friendlyError({
                error: result.message.slice(0, colonIdx).trim(),
                details: result.message.slice(colonIdx + 1).trim(),
              }),
            );
          } else {
            setInlineError(friendlyError({ error: result.message }));
          }
          return;
        }
        case 'preview_failed':
          setInlineError(
            friendlyError({
              error: 'sitemap_fetch_failed',
              reason: result.message,
            }),
          );
          return;
        case 'unknown_error':
          setInlineError({
            message: `Unexpected ${result.status}`,
            detail: result.message,
          });
          return;
      }
    } catch (err) {
      setInlineError({
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setPending(false);
    }
  }

  const total = budgetExceeded
    ? budgetExceeded.tier_preview.tier_1 +
      budgetExceeded.tier_preview.tier_2 +
      budgetExceeded.tier_preview.tier_3 +
      budgetExceeded.tier_preview.tier_4
    : 0;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="start-analysis-title"
      data-testid="start-analysis-dialog"
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6"
      >
        <h2
          id="start-analysis-title"
          className="text-base font-semibold text-slate-900"
        >
          Start coverage analysis
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Triage <strong>{brand.name}</strong>&rsquo;s sitemap and kick off a
          fresh coverage analysis. The estimated cost is computed when you
          submit — if it exceeds your monthly cap you&rsquo;ll see a preview
          before anything is persisted.
        </p>

        {/* Sitemap URL */}
        <label className="mt-4 block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Sitemap URL</span>
          <input
            ref={firstFocusableRef as RefObject<HTMLInputElement>}
            type="url"
            data-testid="start-analysis-sitemap-url"
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
            placeholder={placeholderForInput}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
          <span className="mt-1 block text-xs text-slate-500">
            {brand.sitemap_url ? (
              <>
                Leave blank to use the URL configured on this brand:{' '}
                <span className="font-mono">{brand.sitemap_url}</span>
              </>
            ) : brand.domain ? (
              <>
                Leave blank to use{' '}
                <span className="font-mono">{synthesizedDefault}</span>. Configure
                a permanent value in Brand settings.
              </>
            ) : (
              <>This brand has no domain configured — sitemap URL is required.</>
            )}
          </span>
          {(brand.sitemap_url || brand.domain) && (
            <button
              type="button"
              onClick={() => setSitemapUrl(placeholderUrl)}
              data-testid="start-analysis-use-default"
              className="mt-1 text-xs font-medium text-violet-600 hover:text-violet-800"
            >
              Use brand default
            </button>
          )}
        </label>

        {/* Tier 3 budget slider */}
        <label className="mt-4 block text-sm">
          <span className="mb-1 flex items-center justify-between font-medium text-slate-700">
            <span>Tier 3 budget</span>
            <span className="tabular-nums text-slate-500">{tier3Budget} pages</span>
          </span>
          <input
            type="range"
            data-testid="start-analysis-tier3-budget"
            min={0}
            max={500}
            step={5}
            value={tier3Budget}
            onChange={(e) => setTier3Budget(Number.parseInt(e.target.value, 10))}
            className="w-full accent-violet-600"
          />
          <span className="mt-1 block text-xs text-slate-500">
            Caps how many sampled (Tier 3) pages will be evaluated.
          </span>
        </label>

        {/* Include tier 4 */}
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="start-analysis-include-tier4"
            checked={includeTier4}
            onChange={(e) => setIncludeTier4(e.target.checked)}
            className="h-4 w-4 rounded-md border-slate-300 accent-violet-600"
          />
          <span className="text-slate-700">Include Tier 4 (low-priority) pages</span>
        </label>

        {/* Admin-only override.
            Phase 9 Session E.2 (gap 087-R2-1): the `title` attribute on the
            label gives hover-and-keyboard users an explanation of when this
            bypass is appropriate. Native title is sufficient for v1; if we
            ever need richer formatting we can swap in a dedicated Tooltip
            component without changing the surrounding markup. */}
        {canOverride && (
          <label
            className="mt-3 flex items-center gap-2 text-sm"
            title="Allow this analysis to proceed even if it would exceed the tenant's monthly cost cap. Use only when manually approving an over-budget run."
          >
            <input
              type="checkbox"
              data-testid="start-analysis-override"
              checked={allowOverride}
              onChange={(e) => setAllowOverride(e.target.checked)}
              aria-describedby="start-analysis-override-help"
              title="Allow this analysis to proceed even if it would exceed the tenant's monthly cost cap. Use only when manually approving an over-budget run."
              className="h-4 w-4 rounded-md border-slate-300 accent-violet-600"
            />
            <span className="text-slate-700">
              Bypass monthly budget cap (admin)
            </span>
            <span id="start-analysis-override-help" className="sr-only">
              Allows this analysis to proceed even if it would exceed the
              tenant&apos;s monthly cost cap. Use only when manually approving
              an over-budget run.
            </span>
          </label>
        )}

        {/* Inline validation error */}
        {inlineError && (
          <div
            role="alert"
            data-testid="start-analysis-dialog-error"
            className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
          >
            <p className="font-semibold">{inlineError.message}</p>
            {inlineError.detail && (
              <p className="mt-1 text-xs text-rose-700">{inlineError.detail}</p>
            )}
          </div>
        )}

        {/* Budget-exceeded banner */}
        {budgetExceeded && (
          <div
            data-testid="start-analysis-budget-exceeded-banner"
            className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          >
            <p className="font-medium">Monthly budget would be exceeded.</p>
            <p className="mt-1">
              This analysis would cost{' '}
              <strong>${budgetExceeded.estimated_cost_usd.toFixed(2)}</strong>{' '}
              but your monthly cap is{' '}
              <strong>
                ${budgetExceeded.monthly_cost_cap_usd.toFixed(2)}
              </strong>{' '}
              and you have already spent{' '}
              <strong>${budgetExceeded.monthly_spend_so_far.toFixed(2)}</strong>.
            </p>
            <p className="mt-1 text-xs">
              Would evaluate{' '}
              <strong>{budgetExceeded.tier_preview.to_evaluate}</strong> of{' '}
              <strong>{total}</strong> pages (T1 {budgetExceeded.tier_preview.tier_1},
              T2 {budgetExceeded.tier_preview.tier_2}, T3{' '}
              {budgetExceeded.tier_preview.tier_3}, T4{' '}
              {budgetExceeded.tier_preview.tier_4}).
            </p>
            {canOverride && (
              <p className="mt-2 text-xs">
                Tick &ldquo;Bypass monthly budget cap&rdquo; above and resubmit.
              </p>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={pending} data-testid="start-analysis-submit">
            {pending && <Spinner />}
            {pending ? 'Starting…' : 'Start analysis'}
          </Button>
        </div>
      </form>
    </div>
  );
}
